import { PurchaseTypeEnum } from 'api-hooks/order/common/model';
import { NavigationRoutes } from 'common/constants';
import { PurchaseTransactionTypeEnum, EnumList } from 'common/constants/enum';
import stylesColors from 'common/styles/styles.colors';
import { string2money } from 'common/utils/string';
import notification from 'components/common/notification';
import Separator from 'components/common/separator';
import { Input } from 'components/elements';
import { Button } from 'components/elements/buttons';
import Text from 'components/elements/text';
import useNavigation from 'hooks/use-navigation';
import AgentSelectInput from 'legacy-modules/master/agent/components/select-input';
import PaymentChannelSelectInput from 'legacy-modules/master/payment-channel/components/select-input';
import EnumSelectInput from 'legacy-modules/master/select-input/enum';
import {
  CardSection,
  FullContainer,
  HalfContainer,
  // OneQuarterContainer,
  SectionContainer,
  // SeparatorVertical,
} from 'legacy-modules/styles';
import TourListSelectInput from 'legacy-modules/tour/tour-list/components/select-input';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { OrderAdjustmentFormType } from './form-type';

export default function PurchaseDetailCard() {
  const { t } = useTranslation();
  const { control } = useFormContext<OrderAdjustmentFormType>();
  const { navigate } = useNavigation();

  const [purchase] = useWatch({
    control,
    name: ['purchase'],
  });
  const transactionOrderType = purchase?.transactionType;
  const id = purchase?.id;
  const type = purchase?.type;

  const onNavigate = React.useCallback(() => {
    let target:
      | NavigationRoutes.ActivityPurchasesView
      | NavigationRoutes.ActivityPurchasesView
      | NavigationRoutes.CruisePurchasesView
      | NavigationRoutes.DocumentPurchasesView
      | NavigationRoutes.FlightPurchasesView
      | NavigationRoutes.HotelPurchasesView
      | NavigationRoutes.ProductPurchasesView
      | undefined;

    switch (transactionOrderType) {
      case PurchaseTransactionTypeEnum.Activity:
        target = NavigationRoutes.ActivityPurchasesView;
        break;
      case PurchaseTransactionTypeEnum.Cruise:
        target = NavigationRoutes.CruisePurchasesView;
        break;
      case PurchaseTransactionTypeEnum.Document:
        target = NavigationRoutes.DocumentPurchasesView;
        break;
      case PurchaseTransactionTypeEnum.Flight:
        target = NavigationRoutes.FlightPurchasesView;
        break;
      case PurchaseTransactionTypeEnum.Hotel:
        target = NavigationRoutes.HotelPurchasesView;
        break;
      case PurchaseTransactionTypeEnum.Product:
        target = NavigationRoutes.ProductPurchasesView;
        break;
      default:
        notification.error({
          message: t('refund:no_selected_extra', {
            extra: t('common:order_number'),
          }),
        });
    }

    if (!target) return;
    if (!id) return;

    navigate(target, {
      id,
    });
  }, [transactionOrderType, navigate, id, t]);

  const informationComponent = React.useMemo(() => {
    switch (type) {
      case PurchaseTypeEnum.Sales:
      case PurchaseTypeEnum.Product:
        return (
          <Input
            type="text"
            name="purchase.invoiceNumber"
            label={t('common:invoice_number')}
            placeholder={t('common:choose_invoice')}
            disabled
          />
        );
      case PurchaseTypeEnum.Tour:
        return (
          <TourListSelectInput name="purchase.tour.id" required disabled />
        );
      default:
        return null;
    }
  }, [t, type]);

  return (
    <CardSection>
      <FullContainer>
        <FullContainer $style={{ display: 'flex' }}>
          <Text $style={{ flex: 1 }} variant="HeadingXSmall">
            {t('common:purchase_details')}
          </Text>
          <Button onClick={onNavigate} variant="small" buttonType="secondary">
            {t('common:view_details')}
          </Button>
        </FullContainer>
        <SectionContainer>
          <HalfContainer>
            <Input
              type="date-time"
              name="purchase.transactionAt"
              label={t('common:date')}
              placeholder={t('common:date')}
              disabled
            />
          </HalfContainer>
          <HalfContainer>
            <Input
              type="text"
              name="purchase.number"
              label={t('common:order_number')}
              placeholder={t('common:order_number')}
              disabled
            />
          </HalfContainer>
        </SectionContainer>
        <SectionContainer>
          <HalfContainer>
            <EnumSelectInput
              label={t('common:type')}
              placeholder={t('common:type')}
              enumType={EnumList.PurchaseType}
              name="purchase.type"
              disabled
            />
          </HalfContainer>
          <HalfContainer>{informationComponent}</HalfContainer>
        </SectionContainer>
        <SectionContainer>
          <HalfContainer>
            <AgentSelectInput name="purchase.agent.id" disabled />
          </HalfContainer>
          <HalfContainer>
            <PaymentChannelSelectInput
              name="purchase.paymentChannel.id"
              disabled
            />
          </HalfContainer>
        </SectionContainer>
        <Separator gap={16} direction="vertical" />
        <FullContainer $style={{ display: 'flex', alignItems: 'center' }}>
          <Text $style={{ flex: 1 }} variant="LabelSmall">
            {t('common:total_purchase')}
          </Text>
          <Text variant="HeadingXSmall" color={stylesColors.primaryDefault}>
            Rp. {purchase?.total ? string2money(purchase.total) : 0}
          </Text>
        </FullContainer>
      </FullContainer>
    </CardSection>
  );
}
